import type { Player } from '../types/player';
import type { LeagueFormatType } from '../components/LeagueFormatSelector';

export interface SnakePick {
  pick: number;
  team: number;
  round: number;
  pickInRound: number;
}

export interface RosterSlotSpec {
  name: string;
  count: number;
}

export interface RosterConfig {
  positions: RosterSlotSpec[];
  benchSize: number;
}

export function snakeLocation(pick: number, teamCount: number): SnakePick {
  const safeTeams = Math.max(1, teamCount);
  const safePick = Math.max(1, pick);
  const round = Math.floor((safePick - 1) / safeTeams) + 1;
  const pickInRound = ((safePick - 1) % safeTeams) + 1;
  const team = round % 2 === 1 ? pickInRound : safeTeams - pickInRound + 1;
  return { pick: safePick, team, round, pickInRound };
}

export function firstSnakePicks(seat: number, teamCount: number, rounds: number): number[] {
  const picks: number[] = [];
  for (let round = 1; round <= rounds; round += 1) {
    if (round % 2 === 1) {
      picks.push((round - 1) * teamCount + seat);
    } else {
      picks.push((round - 1) * teamCount + (teamCount - seat + 1));
    }
  }
  return picks;
}

export function upcomingPicks(
  currentPick: number,
  teamCount: number,
  userSeat: number,
  count: number
): Array<SnakePick & { yours: boolean }> {
  return Array.from({ length: count }, (_, index) => {
    const location = snakeLocation(currentPick + index + 1, teamCount);
    return { ...location, yours: location.team === userSeat };
  });
}

export function rosterConfigForFormat(format: LeagueFormatType): RosterConfig {
  switch (format) {
    case 'superflex':
      return {
        positions: [
          { name: 'QB', count: 1 },
          { name: 'RB', count: 2 },
          { name: 'WR', count: 2 },
          { name: 'TE', count: 1 },
          { name: 'FLEX', count: 1 },
          { name: 'SUPERFLEX', count: 1 },
          { name: 'K', count: 1 },
          { name: 'DEF', count: 1 },
        ],
        benchSize: 6,
      };
    case '2qb':
      return {
        positions: [
          { name: 'QB', count: 2 },
          { name: 'RB', count: 2 },
          { name: 'WR', count: 2 },
          { name: 'TE', count: 1 },
          { name: 'FLEX', count: 1 },
          { name: 'K', count: 1 },
          { name: 'DEF', count: 1 },
        ],
        benchSize: 6,
      };
    case 'bestball':
      return {
        positions: [
          { name: 'QB', count: 1 },
          { name: 'RB', count: 2 },
          { name: 'WR', count: 3 },
          { name: 'TE', count: 1 },
          { name: 'FLEX', count: 2 },
        ],
        benchSize: 9,
      };
    case 'dynasty':
      return {
        positions: [
          { name: 'QB', count: 1 },
          { name: 'RB', count: 2 },
          { name: 'WR', count: 3 },
          { name: 'TE', count: 1 },
          { name: 'FLEX', count: 2 },
        ],
        benchSize: 12,
      };
    default:
      return {
        positions: [
          { name: 'QB', count: 1 },
          { name: 'RB', count: 2 },
          { name: 'WR', count: 2 },
          { name: 'TE', count: 1 },
          { name: 'FLEX', count: 1 },
          { name: 'K', count: 1 },
          { name: 'DEF', count: 1 },
        ],
        benchSize: 6,
      };
  }
}

export function slotEligibility(position: string): string[] {
  const slots = [position];
  if (position === 'RB' || position === 'WR' || position === 'TE') slots.push('FLEX');
  if (position === 'QB' || position === 'RB' || position === 'WR' || position === 'TE') {
    slots.push('SUPERFLEX');
  }
  return slots;
}

export function assignRosterSlots(
  roster: Player[],
  format: LeagueFormatType
): { starters: Record<string, Array<Player | null>>; bench: Player[] } {
  const config = rosterConfigForFormat(format);
  const starters: Record<string, Array<Player | null>> = {};
  config.positions.forEach((spec) => {
    starters[spec.name] = Array(spec.count).fill(null);
  });

  const assigned = new Set<string>();
  const sorted = [...roster].sort((a, b) => (b.value || b.projection || 0) - (a.value || a.projection || 0));

  for (const player of sorted) {
    for (const slotName of slotEligibility(player.position)) {
      const slots = starters[slotName];
      if (!slots) continue;
      const empty = slots.findIndex((slot) => slot === null);
      if (empty === -1) continue;
      slots[empty] = player;
      assigned.add(String(player.id));
      break;
    }
  }

  return {
    starters,
    bench: roster.filter((player) => !assigned.has(String(player.id))),
  };
}

export function openStarterHoles(roster: Player[], format: LeagueFormatType): string[] {
  const { starters } = assignRosterSlots(roster, format);
  const holes: string[] = [];
  for (const [slot, players] of Object.entries(starters)) {
    if (players.some((player) => player === null)) holes.push(slot);
  }
  return holes;
}

export interface DraftRecommendation {
  player: Player;
  reason: string;
}

export function recommendPicks(
  available: Player[],
  roster: Player[],
  format: LeagueFormatType,
  limit: number = 3
): DraftRecommendation[] {
  const holes = openStarterHoles(roster, format);
  const ranked = [...available].sort((a, b) => (a.rank || 999) - (b.rank || 999));

  const scored = ranked.map((player) => {
    const directHole = holes.includes(player.position);
    const flexHole = !directHole && slotEligibility(player.position).some((slot) => holes.includes(slot));
    const qbPremium = (format === 'superflex' || format === '2qb') && player.position === 'QB' && holes.includes('QB');
    const score = (player.rank || 999) - (directHole ? 24 : 0) - (flexHole ? 8 : 0) - (qbPremium ? 10 : 0);
    let reason = 'Best available';
    if (qbPremium) reason = 'Fills a QB seat';
    else if (directHole) reason = `Fills ${player.position}`;
    else if (flexHole) reason = 'Fits a flex hole';
    return { player, reason, score };
  });

  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map(({ player, reason }) => ({ player, reason }));
}

export function formatLabel(format: LeagueFormatType): string {
  if (format === '2qb') return '2QB';
  if (format === 'bestball') return 'Best ball';
  if (format === 'superflex') return 'Superflex';
  if (format === 'dynasty') return 'Dynasty';
  return 'Standard';
}
